'use client';

import { useTranslations } from 'next-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme, alpha } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ChatIcon from '@mui/icons-material/Chat';
import ShareIcon from '@mui/icons-material/Share';

/**
 * How It Works - 3-step visual process
 *
 * Shows: Dump → Test → Share
 *
 * Used primarily in Variant B (Mobile-First Vertical)
 * but can be used in other variants as well.
 *
 * Story 6.1: Marketing Landing Page with A/B Variants
 */

interface Step {
  key: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface HowItWorksProps {
  /** Layout direction */
  direction?: 'horizontal' | 'vertical';
  /** Show step numbers */
  showNumbers?: boolean;
  /** Compact mode for smaller spaces */
  compact?: boolean;
}

export function HowItWorks({
  direction: propDirection,
  showNumbers = true,
  compact = false,
}: HowItWorksProps) {
  const t = useTranslations('creator.landing.howItWorks');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Auto-detect direction based on viewport if not specified
  const direction = propDirection || (isMobile ? 'vertical' : 'horizontal');

  const steps: Step[] = [
    {
      key: 'dump',
      icon: <CloudUploadIcon />,
      title: t('steps.dump.title'),
      description: t('steps.dump.description'),
    },
    {
      key: 'test',
      icon: <ChatIcon />,
      title: t('steps.test.title'),
      description: t('steps.test.description'),
    },
    {
      key: 'share',
      icon: <ShareIcon />,
      title: t('steps.share.title'),
      description: t('steps.share.description'),
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: direction === 'horizontal' ? 'row' : 'column',
        gap: { xs: 2, md: 3 },
        justifyContent: 'center',
        alignItems: direction === 'horizontal' ? 'stretch' : 'center',
        width: '100%',
      }}
    >
      {steps.map((step, index) => (
        <StepCard
          key={step.key}
          step={step}
          index={index}
          showNumber={showNumbers}
          compact={compact}
          direction={direction}
        />
      ))}
    </Box>
  );
}

interface StepCardProps {
  step: Step;
  index: number;
  showNumber: boolean;
  compact: boolean;
  direction: 'horizontal' | 'vertical';
}

function StepCard({ step, index, showNumber, compact, direction }: StepCardProps) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        flex: direction === 'horizontal' ? 1 : undefined,
        width: direction === 'vertical' ? '100%' : undefined,
        maxWidth: direction === 'vertical' ? 400 : undefined,
        p: compact ? 2 : 3,
        display: 'flex',
        flexDirection: direction === 'horizontal' ? 'column' : 'row',
        alignItems: direction === 'horizontal' ? 'center' : 'flex-start',
        gap: 2,
        textAlign: direction === 'horizontal' ? 'center' : 'start',
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.1),
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          transform: 'translateY(-2px)',
        },
      }}
    >
      {/* Icon with optional number badge */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: compact ? 48 : 56,
          height: compact ? 48 : 56,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          flexShrink: 0,
          '& .MuiSvgIcon-root': {
            fontSize: compact ? '1.5rem' : '1.75rem',
          },
        }}
      >
        {step.icon}
        {showNumber && (
          <Box
            sx={{
              position: 'absolute',
              top: -4,
              right: -4,
              width: 20,
              height: 20,
              borderRadius: '50%',
              bgcolor: 'secondary.main',
              color: 'secondary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 700,
            }}
          >
            {index + 1}
          </Box>
        )}
      </Box>

      {/* Text content */}
      <Box sx={{ flex: 1 }}>
        <Typography
          variant={compact ? 'subtitle2' : 'subtitle1'}
          fontWeight={600}
          gutterBottom={!compact}
          sx={{ mb: compact ? 0.5 : undefined }}
        >
          {step.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: compact ? '0.8125rem' : '0.875rem',
            lineHeight: 1.5,
          }}
        >
          {step.description}
        </Typography>
      </Box>
    </Paper>
  );
}
